import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  PaginatedResponse
} from '../types';
import { getApiBaseUrl } from '../config/environment';
import { offlineStorage } from './offlineStorage';
import { syncService } from './syncService';

// Types pour les variables d'environnement Vite
declare global {
  interface ImportMetaEnv {
    readonly VITE_API_BASE_URL: string;
    readonly VITE_APP_NAME: string;
    readonly VITE_APP_VERSION: string;
    readonly VITE_DEBUG: string;
  }

  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

// Importer le service de pesée
import weighingService from './weighing';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
  }> = [];
  private offlineMode = false;

  constructor() {
    this.baseURL = getApiBaseUrl();
    
    // Forcer l'URL locale pour le développement
    if ((import.meta.env as any).MODE === 'development') {
      this.baseURL = 'http://localhost:8000/api/v1';
    }
    
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000, // 30 secondes
    });

    // Initialiser le mode offline
    this.initializeOfflineMode();

    // Intercepteur pour ajouter le token d'authentification et les headers multi-tenant
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Ajouter les headers multi-tenant
        const activeSiteCode = localStorage.getItem('activeSiteCode');
        if (activeSiteCode) {
          // Récupérer les informations du site depuis le contexte ou localStorage
          const siteInfo = localStorage.getItem('activeSiteInfo');
          if (siteInfo) {
            try {
              const site = JSON.parse(siteInfo);
              config.headers['x-tenant-id'] = site.tenant.id.toString();
              config.headers['x-site-id'] = site.id.toString();
            } catch (error) {
              console.warn('Erreur lors du parsing des informations du site:', error);
            }
          }
        }
        
        // Cache-busting temporaire pour forcer le rechargement
        if (config.method === 'get') {
          config.params = config.params || {};
          config.params._t = Date.now();
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Intercepteur pour gérer les erreurs d'authentification
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Gestion des erreurs 401 - Ne plus déconnecter automatiquement
        // Les tokens n'expirent plus automatiquement, on tente toujours de rafraîchir
        if (error.response?.status === 401 && !originalRequest._retry) {
          // Si c'est déjà une requête de refresh qui échoue, ne pas déconnecter
          // On laisse l'utilisateur continuer à utiliser l'application
          if (originalRequest.url?.includes('/auth/token/refresh/')) {
            // Échec du refresh : on rejette sans alerter l'utilisateur (pas de modal)
            return Promise.reject(error);
          }
          
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken && !this.isRefreshing) {
            originalRequest._retry = true;
            this.isRefreshing = true;
            
            try {
              const response = await this.refreshToken({ refresh: refreshToken });
              // Backend retourne { success, message, data: { access, refresh } }
              const newAccess = response.data?.data?.access ?? response.data?.access;
              const newRefresh = response.data?.data?.refresh ?? response.data?.refresh;

              if (newAccess) {
                localStorage.setItem('access_token', newAccess);
                if (newRefresh) {
                  localStorage.setItem('refresh_token', newRefresh);
                }
                originalRequest.headers.Authorization = `Bearer ${newAccess}`;

                this.processQueue(null, newAccess);
                return this.api.request(originalRequest);
              } else {
                console.warn('⚠️ Réponse de refresh sans token d\'accès');
                throw new Error('Token de rafraîchissement invalide');
              }
            } catch (refreshError) {
              this.processQueue(refreshError, null);
              // Pas d'alerte utilisateur : la requête échoue silencieusement
              return Promise.reject(refreshError);
            } finally {
              this.isRefreshing = false;
            }
          } else if (this.isRefreshing) {
            // Si un refresh est déjà en cours, mettre en queue
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.api.request(originalRequest);
            }).catch(err => {
              return Promise.reject(err);
            });
          } else {
            // Pas de refresh token : on rejette sans alerter l'utilisateur
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Méthodes d'authentification
 
  async refreshToken(data: any): Promise<AxiosResponse<any>> {
    return this.api.post('/auth/token/refresh/', data);
  }

  async logout(): Promise<void> {
    // Nettoyer tous les tokens et données d'authentification
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('activeSiteCode');
    localStorage.removeItem('activeSiteInfo');
    
    // Nettoyer aussi sessionStorage
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('skip-login-redirect');
    sessionStorage.removeItem('activeSiteCode');
    sessionStorage.removeItem('activeSiteInfo');
    
    // Nettoyer les headers multi-tenant
    this.clearTenantHeaders();
    
    // Réinitialiser le flag de refresh
    this.isRefreshing = false;
    this.failedQueue = [];
  }

  private processQueue(error: any, token: string | null = null): void {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    this.failedQueue = [];
  }

  private clearAuthAndRedirect(): void {
    // Nettoyer complètement toutes les données d'authentification
    this.logout();
    
    // S'assurer que tous les tokens sont bien supprimés
    try {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('activeSiteCode');
      localStorage.removeItem('activeSiteInfo');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      sessionStorage.removeItem('skip-login-redirect');
      sessionStorage.removeItem('activeSiteCode');
      sessionStorage.removeItem('activeSiteInfo');
    } catch (error) {
      console.error('Erreur lors du nettoyage des tokens:', error);
    }
    
    // Rediriger vers la page de connexion (forcer le rechargement même si déjà sur /login)
    const currentPath = window.location.pathname;
    if (currentPath !== '/login') {
      // Si on n'est pas sur la page de login, rediriger normalement
      window.location.href = '/login';
    } else {
      // Si on est déjà sur la page de login, forcer un rechargement complet
      window.location.reload();
    }
  }

  // Service de pesée - accessible via this.weighing
  get weighing() {
    return weighingService;
  }



  // Méthodes existantes pour les tickets de pesée (compatibilité)
  async getWeighingTickets(params?: any): Promise<AxiosResponse<PaginatedResponse<any>>> {
    return this.api.get('/weighing/api/weighing-tickets/', { params });
  }

  async getWeighingTicket(id: number): Promise<AxiosResponse<any>> {
    return this.api.get(`/weighing/api/weighing-tickets/${id}/`);
  }

  async createWeighingTicket(data: any): Promise<AxiosResponse<any>> {
    return this.api.post('/weighing/api/weighing-tickets/', data);
  }

  async updateWeighingTicket(id: number, data: any): Promise<AxiosResponse<any>> {
    return this.api.patch(`/weighing/api/weighing-tickets/${id}/`, data);
  }

  async deleteWeighingTicket(id: number): Promise<AxiosResponse<void>> {
    return this.api.delete(`/weighing/api/weighing-tickets/${id}/`);
  }

  // Actions spéciales pour les tickets de pesée existants
  async completeWeighingTicket(id: number, data: { tare_weight: number }): Promise<AxiosResponse<any>> {
    return this.api.post(`/weighing/api/weighing-tickets/${id}/complete/`, data);
  }

  async rejectWeighingTicket(id: number, data: { reason: string }): Promise<AxiosResponse<any>> {
    return this.api.post(`/weighing/api/weighing-tickets/${id}/reject/`, data);
  }

  async getPendingTareTickets(): Promise<AxiosResponse<any[]>> {
    return this.api.get('/weighing/api/weighing-tickets/pending-tare/');
  }

  async getWeighingTicketStatistics(): Promise<AxiosResponse<any>> {
    return this.api.get('/weighing/api/weighing-tickets/statistics/');
  }

  // Méthodes utilitaires
  getBaseURL(): string {
    return this.baseURL;
  }

  setAuthToken(token: string): void {
    localStorage.setItem('access_token', token);
  }

  getAuthToken(): string | null {
    return localStorage.getItem('access_token');
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }

  // Méthodes pour gérer les headers multi-tenant
  setTenantHeaders(tenantId: number, siteId: number): void {
    this.api.defaults.headers['x-tenant-id'] = tenantId.toString();
    this.api.defaults.headers['x-site-id'] = siteId.toString();
  }

  clearTenantHeaders(): void {
    delete this.api.defaults.headers['x-tenant-id'];
    delete this.api.defaults.headers['x-site-id'];
  }

  // Méthodes pour gérer le mode offline
  private async initializeOfflineMode(): Promise<void> {
    try {
      await offlineStorage.init();
      this.offlineMode = await offlineStorage.getOfflineMode();
      
      // Écouter les changements de mode offline
      window.addEventListener('offline-mode-changed', (event: Event) => {
        const customEvent = event as CustomEvent;
        this.offlineMode = customEvent.detail.enabled;
      });
    } catch (error) {
      console.error('Erreur initialisation mode offline:', error);
    }
  }

  // Vérifier si une URL contient des données de référence
  private isReferenceData(url: string): boolean {
    const referenceEndpoints = [
      '/weighing/api/camions/',
      '/weighing/api/chauffeurs/',
      '/weighing/api/produits/',
      '/weighing/api/clients/',
      '/weighing/api/fournisseurs/',
      '/weighing/api/exportateurs/',
      '/weighing/api/transporteurs/'
    ];
    return referenceEndpoints.some(endpoint => url.includes(endpoint));
  }

  // Vérifier si on est en mode offline
  private isOfflineMode(): boolean {
    return this.offlineMode || !navigator.onLine;
  }

  // Méthodes HTTP adaptées pour le mode offline
  async get(url: string, config?: any) {
    try {
      // Essayer d'abord la requête réseau
      const response = await this.api.get(url, config);
      
      // Mettre en cache les données de référence
      if (this.isReferenceData(url)) {
        const data = response.data.results || response.data;
        if (Array.isArray(data)) {
          for (const item of data) {
            await offlineStorage.saveData(this.getStoreNameFromUrl(url), item);
          }
        }
      }
      
      return response;
    } catch (error) {
      // En cas d'erreur réseau, essayer de récupérer depuis le cache
      if (this.isOfflineMode() && this.isReferenceData(url)) {
        const cachedData = await offlineStorage.getData(this.getStoreNameFromUrl(url));
        if (cachedData) {
          return { 
            data: Array.isArray(cachedData) ? { results: cachedData, count: cachedData.length } : cachedData,
            fromCache: true 
          };
        }
      }
      throw error;
    }
  }

  async post(url: string, data?: any, config?: any) {
    try {
      const response = await this.api.post(url, data, config);
      
      // Sauvegarder localement si c'est une création importante
      if (this.shouldCacheResponse(url)) {
        await this.cacheResponse(url, response.data);
      }
      
      return response;
    } catch (error) {
      if (this.isOfflineMode()) {
       
        // Sauvegarder pour synchronisation future
        await syncService.addToSyncQueue('CREATE', url, data);
        
        // Créer une réponse locale
        const localResponse = this.createLocalResponse(data);
        await this.cacheResponse(url, localResponse);
        
        return { data: localResponse, fromCache: true };
      }
      throw error;
    }
  }

  async put(url: string, data?: any, config?: any) {
    try {
      const response = await this.api.put(url, data, config);
      
      if (this.shouldCacheResponse(url)) {
        await this.cacheResponse(url, response.data);
      }
      
      return response;
    } catch (error) {
      if (this.isOfflineMode()) {
        
        await syncService.addToSyncQueue('UPDATE', url, data);
        
        const localResponse = this.createLocalResponse(data);
        await this.cacheResponse(url, localResponse);
        
        return { data: localResponse, fromCache: true };
      }
      throw error;
    }
  }

  async patch(url: string, data?: any, config?: any) {
    try {
      const response = await this.api.patch(url, data, config);
      
      if (this.shouldCacheResponse(url)) {
        await this.cacheResponse(url, response.data);
      }
      
      return response;
    } catch (error) {
      if (this.isOfflineMode()) {
        await syncService.addToSyncQueue('UPDATE', url, data);
        
        const localResponse = this.createLocalResponse(data);
        await this.cacheResponse(url, localResponse);
        
        return { data: localResponse, fromCache: true };
      }
      throw error;
    }
  }

  async delete(url: string, config?: any) {
    try {
      const response = await this.api.delete(url, config);
      
      // Supprimer du cache local uniquement pour les stores connus
      const storeName = this.getStoreNameFromUrl(url);
      if (storeName !== 'unknown') {
        const id = this.extractIdFromUrl(url);
        if (id) {
          await offlineStorage.deleteData(storeName, id);
        }
      }
      
      return response;
    } catch (error) {
      if (this.isOfflineMode()) {
       
        const id = this.extractIdFromUrl(url);
        if (id) {
          await syncService.addToSyncQueue('DELETE', url, { id });
        }
        
        return { data: { success: true, message: 'Suppression programmée pour synchronisation' }, fromCache: true };
      }
      throw error;
    }
  }

  // Méthodes utilitaires pour le cache
  private getStoreNameFromUrl(url: string): string {
    if (url.includes('/camions/')) return 'camions';
    if (url.includes('/chauffeurs/')) return 'chauffeurs';
    if (url.includes('/produits/')) return 'produits';
    if (url.includes('/clients/')) return 'clients';
    if (url.includes('/fournisseurs/')) return 'fournisseurs';
    if (url.includes('/exportateurs/')) return 'exportateurs';
    if (url.includes('/transporteurs/')) return 'transporteurs';
    if (url.includes('/pesees/')) return 'pesees';
    if (url.includes('/tickets/')) return 'tickets';
    return 'unknown';
  }


  private async cacheResponse(url: string, data: any): Promise<void> {
    const storeName = this.getStoreNameFromUrl(url);
    if (storeName !== 'unknown' && data) {
      try {
        // Vérifier que les données sont valides avant de les sauvegarder
        if (this.shouldCacheResponse(data)) {
          await offlineStorage.saveData(storeName, data);
        }
      } catch (error) {
        console.warn(`⚠️ Impossible de mettre en cache les données pour ${url}:`, error);
        // Ne pas faire échouer la requête principale à cause du cache
      }
    }
  }

  /**
   * Vérifie si les données doivent être mises en cache
   */
  private shouldCacheResponse(data: any): boolean {
    // Ne pas mettre en cache les données vides ou nulles
    if (!data) return false;
    
    // Ne pas mettre en cache les erreurs
    if (data.error || data.message?.includes('error')) return false;
    
    // Pour les tableaux, vérifier qu'ils ne sont pas vides
    if (Array.isArray(data)) {
      return data.length > 0;
    }
    
    // Pour les objets, vérifier qu'ils ont au moins une propriété
    if (typeof data === 'object') {
      return Object.keys(data).length > 0;
    }
    
    return true;
  }

  private createLocalResponse(data: any): any {
    const id = this.generateTempId();
    const now = new Date().toISOString();
    
    return {
      ...data,
      id,
      created_at: now,
      updated_at: now,
      isOffline: true,
      temp_id: id
    };
  }

  private generateTempId(): string {
    return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private extractIdFromUrl(url: string): string | null {
    const match = url.match(/\/(\d+)\/?$/);
    return match ? match[1] : null;
  }

  // Méthodes publiques pour gérer le mode offline
  async setOfflineMode(enabled: boolean): Promise<void> {
    this.offlineMode = enabled;
    await offlineStorage.setOfflineMode(enabled);
    
    // Émettre l'événement de changement de mode
    window.dispatchEvent(new CustomEvent('offline-mode-changed', { 
      detail: { enabled } 
    }));
  }

  getOfflineMode(): boolean {
    return this.offlineMode;
  }

  async getOfflineData(storeName: string): Promise<any[]> {
    return await offlineStorage.getData(storeName);
  }

  async getPendingChangesCount(): Promise<number> {
    return await offlineStorage.getPendingChangesCount();
  }
}

// Instance singleton du service API
const apiService = new ApiService();
export default apiService;